import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { Seo } from "../components/Seo";
import { SlotPicker } from "../components/SlotPicker";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Calendar } from "lucide-react";

const Input = ({ label, value, onChange, type = "text", testid, required }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#8A939B]">{label}{required && <span className="text-[#C6FF00]"> *</span>}</span>
    <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border border-[#23262c] bg-[#14161b] px-4 py-3 text-white outline-none focus:border-[#C6FF00]" />
  </label>
);

export default function BookCall() {
  const [slots, setSlots] = useState(null);
  const [f, setF] = useState({ name: "", email: "", business: "", notes: "" });
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [booked, setBooked] = useState(null);

  useEffect(() => { api.get("/acquisition/slots").then((r) => setSlots(r.data.slots)).catch(() => setSlots([])); }, []);

  const valid = f.name && /.+@.+\..+/.test(f.email) && selected;

  const book = async () => {
    setBusy(true);
    try {
      const r = await api.post("/acquisition/strategy-book", { ...f, slot_id: selected });
      setBooked(r.data.call);
    } catch (e) {
      toast.error(apiError(e));
      if (e?.response?.status === 409) {
        const s = await api.get("/acquisition/slots").catch(() => null);
        if (s) setSlots(s.data.slots);
        setSelected(null);
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0C0F] text-white">
      <Seo title="Book Your Strategy Call — HughLaunch" description="Pick a time for your free HughLaunch strategy call. We meet on Google Meet — no obligation, no fluff." path="/book" />
      <div className="mx-auto max-w-2xl px-5 pt-6">
        <a href="/" data-testid="book-home-logo" className="inline-flex items-center gap-2">
          <img src="/hl-logo-v2.png" alt="HughLaunch" className="h-8 w-auto" />
          <span className="text-lg font-extrabold">Hugh<span className="text-[#C6FF00]">Launch</span></span>
        </a>
      </div>
      <div className="mx-auto max-w-2xl px-5 py-14 lg:py-20">
        {booked ? (
          <div className="text-center" data-testid="book-confirmed">
            <CheckCircle2 className="mx-auto h-14 w-14 text-[#C6FF00]" />
            <h1 className="mt-6 text-3xl font-extrabold sm:text-4xl">You're booked.</h1>
            <p className="mx-auto mt-4 max-w-md text-[#8A939B]">Your strategy call is confirmed for <span className="font-bold text-white">{booked.start_display}</span>. We'll meet on Google Meet — a confirmation email and calendar invite with the meeting link are on their way.</p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              {booked.meet_url && (
                <a href={booked.meet_url} target="_blank" rel="noreferrer" data-testid="book-meet-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-[#C6FF00] px-7 py-3.5 font-extrabold text-[#C6FF00] transition-colors hover:bg-[#C6FF00] hover:text-[#0A0C0F]">
                  Join Google Meet
                </a>
              )}
              <a href={booked.gcal_url} target="_blank" rel="noreferrer" data-testid="book-gcal-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#C6FF00] px-7 py-3.5 font-extrabold text-[#0A0C0F] transition-transform hover:scale-[1.02]">
                <Calendar className="h-4 w-4" /> Add to Google Calendar
              </a>
            </div>
          </div>
        ) : (
          <div data-testid="book-form">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C6FF00]">Free Strategy Call</p>
            <h1 className="mt-3 text-3xl font-extrabold sm:text-4xl">Book your strategy call</h1>
            <p className="mt-4 text-[#8A939B]">Pick a time that works, tell us who you are, and we'll meet on Google Meet. You'll get a calendar invite with the meeting link the moment you book.</p>

            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input label="Name" required value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="book-name" />
              <Input label="Email" required type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} testid="book-email" />
              <div className="sm:col-span-2">
                <Input label="Business name" value={f.business} onChange={(v) => setF({ ...f, business: v })} testid="book-business" />
              </div>
              <label className="block sm:col-span-2">
                <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#8A939B]">What would you like to cover? (optional)</span>
                <textarea data-testid="book-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={3}
                  className="w-full rounded-xl border border-[#23262c] bg-[#14161b] px-4 py-3 text-white outline-none focus:border-[#C6FF00]" />
              </label>
            </div>

            <p className="mt-8 mb-2 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Pick a time</p>
            <SlotPicker slots={slots} selected={selected} onSelect={setSelected} testPrefix="book-slot" />

            <button data-testid="book-submit-btn" disabled={!valid || busy} onClick={book}
              className="mt-8 w-full rounded-full bg-[#C6FF00] px-7 py-4 font-extrabold text-[#0A0C0F] disabled:opacity-30">
              {busy ? "Booking…" : <>Confirm My Strategy Call <ArrowRight className="ml-1 inline h-4 w-4" /></>}
            </button>
            <p className="mt-4 text-center text-xs text-[#5b636b]">All times Pacific · 30 minutes · Google Meet · you can reschedule once if plans change.</p>
          </div>
        )}
      </div>
    </div>
  );
}
